import React, { createRef, FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation, Trans } from '@wix/wix-i18n-config';
import { useBiLogger } from '../../contexts/BILoggerContext';

import s from './MarketplaceTemplatesCarousel.scss';

import { useCarouselScroll } from './hooks/useMarketplaceCarouselScroll';
import { MarketplaceTemplate } from '../MarketplaceTemplate/MarketplaceTemplate';
import { useMarketplaceTemplates } from '../../hooks/useMarketplaceTemplates';
import { useLocation } from 'react-router-dom';

export const MarketplaceTemplatesCarousel: FC = () => {
  const marketplaceTemplates = useMarketplaceTemplates();
  const { t } = useTranslation();
  const biLogger = useBiLogger();
  const { pathname } = useLocation();
  const prevPathname = useRef<string>(pathname);

  const templatesRefs = useMemo(
    () => marketplaceTemplates.map(() => createRef<HTMLDivElement>()),
    [marketplaceTemplates],
  );

  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pathname !== prevPathname.current) {
      prevPathname.current = pathname;
      carouselRef?.current?.scrollTo({ left: 0 });
    }
  }, [pathname]);

  const {
    showNextTemplate,
    showPreviousTemplate,
    isStartPosition,
    isEndPosition,
  } = useCarouselScroll({
    templatesRefs,
    carouselRef,
  });

  const onMarketplaceLinkClick = () => {
    biLogger.reportGeneralAction({
      action: 'explore_template_marketplace',
      actionValue: 'explore_template_marketplace',
    });
  };

  const handleCarouselArrowClick =
    (direction: 'backward' | 'forward') => () => {
      biLogger.reportGeneralAction({
        action: 'browse_marketplace_templates',
        actionValue: direction,
      });
      direction === 'forward' ? showNextTemplate() : showPreviousTemplate();
    };

  return (
    <div className={s.container}>
      <div className={s.textBlock}>
        <div className={s.title}>
          {t('app.templates.marketplace.gallery.title')}
        </div>
        <div className={s.description}>
          <Trans
            i18nKey="app.templates.marketplace.gallery.subtitle"
            components={{
              Link: (
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  href="https://www.wix.com/marketplace/templates?entry=templates-page&origin=templates-marketplace-gallery"
                  target="_blank"
                  className={s.seeAllLink}
                  onClick={onMarketplaceLinkClick}
                  rel="noreferrer"
                />
              ),
            }}
          />
        </div>
      </div>
      <div className={s.carousel} ref={carouselRef}>
        {marketplaceTemplates.map((template, index) => (
          <MarketplaceTemplate
            ref={templatesRefs[index]}
            key={template.id}
            marketplaceTemplate={template}
            index={index}
          />
        ))}
      </div>
      <div className={s.arrowsContainer}>
        <button
          className={s.arrow}
          disabled={isStartPosition}
          onClick={handleCarouselArrowClick('backward')}
        >
          ←
        </button>
        <button
          className={s.arrow}
          disabled={isEndPosition}
          onClick={handleCarouselArrowClick('forward')}
        >
          →
        </button>
      </div>
    </div>
  );
};
